/**
 * Convert a Date object to a YYYY-MM-DD string using local date parts.
 * Prevents timezone-induced day shifts when sending date-only fields to the API.
 */
import axios, { AxiosRequestConfig } from "axios";
import qs from "qs";
import config from "$services/config"; // TODO this should be a name exports, not a default
import logger from "$utils/logger";
import { getToken } from "./token";
import { isProductInAReception } from "../services/Reception.service";

export const toLocalDateString = (
  date: Date | null | undefined
): string | null => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fetchWithToken = async (
  url: string,
  options: AxiosRequestConfig = {},
  signal = null
) => {
  const { CancelToken } = axios;
  const source = CancelToken.source();
  signal?.addEventListener("abort", () => {
    source.cancel("Query was cancelled by React Query");
  });

  try {
    const token = getToken();
    if (!token && !options.headers?.Authorization) {
      throw new Error("No token is set");
    }

    return await axios(url, {
      ...options,
      cancelToken: source.token,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(!options.headers?.["Content-type"]
          ? { "Content-Type": "application/json" }
          : {}),
        ...options.headers,
      },
    });
  } catch (error) {
    logger.error(error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const fetch = (endpoint, options = {}, api_url = "", signal = null) => {
  if (api_url === "") {
    // eslint-disable-next-line no-param-reassign
    api_url = config.api_url;
  }

  const url = `${api_url}${endpoint}`;

  return fetchWithToken(url, options, signal);
};

export const fetchwithoutToken = (
  endpoint: string,
  options: AxiosRequestConfig = {},
  api_url = "",
  signal = null
) => {
  if (api_url === "") {
    // eslint-disable-next-line no-param-reassign
    api_url = config.api_url;
  }

  const url = `${api_url}${endpoint}`;
  const { CancelToken } = axios;
  const source = CancelToken.source();
  signal?.addEventListener("abort", () => {
    source.cancel("Query was cancelled by React Query");
  });

  return axios(url, {
    ...options,
    cancelToken: source.token,
    headers: {
      ...(!options.headers?.["Content-type"]
        ? { "Content-Type": "application/json" }
        : {}),
      ...options.headers,
    },
  });
};

export const getHeaders = (authToken?: any) => {
  const headers: any = {
    "Content-Type": "application/json",
  };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  return headers;
};

export const makeRequest = (
  url: string,
  method: string,
  body?: any,
  headers?: any,
  responseType?: any
) => {
  const options: any = {
    method,
    url,
  };
  if (body) {
    options.data = body;
  }
  if (headers) {
    options.headers = headers;
  }
  if (responseType) {
    options.responseType = responseType;
  }
  return axios(options);
};

export const getUrl = (url: string, populate?: string[], fields?: string) => {
  const query = {} as {
    populate?: string | object;
    fields?: string | object;
  };
  if (populate) {
    query.populate = populate;
  }
  if (fields) {
    query.fields = fields;
  }
  const queryParams = qs.stringify(query, {
    encodeValuesOnly: true,
  });
  return `${url}${queryParams ? "?" : ""}${queryParams}`;
};

export const writetoLS = async (key: string, data: string) => {
  await window.localStorage.setItem(key, data);
};

export const getFromLS = (key: string) => window.localStorage.getItem(key);

export const removeLS = async (data: any) => {
  Object.keys(data).forEach(async (key: string) => {
    window.localStorage.removeItem(key);
  });
};

export const getUserOs = () => {
  if (navigator.appVersion.indexOf("Win") !== -1) {
    return "windows";
  }
  if (navigator.appVersion.indexOf("Mac") !== -1) {
    return "macos";
  }
  if (navigator.appVersion.indexOf("X11") !== -1) {
    return "unix";
  }
  if (navigator.appVersion.indexOf("Linux") !== -1) {
    return "linux";
  }
  return "unknown";
};

/**
 * Check if a product is used in various places:
 * - in the given edbs
 * - in a reception excluding the one provided (excludedReceptionId param)
 */
export const isProductAlreadyUsed = async (
  product: any,
  edbs?: any[],
  excludedReceptionId?: number
) => {
  if (product) {
    let found = false;
    if (edbs) {
      // Check if the product ref is used in an EDB
      edbs.forEach((edb: any) => {
        if (
          edb.products?.findIndex(
            (prod: any) =>
              prod.relatedProduct?._id.toString() === product?._id.toString()
          ) !== -1
        ) {
          found = true;
        }
      });
    }
    if (!found) {
      found = await isProductInAReception(
        product.reference,
        excludedReceptionId
      );
    }
    return found;
  }

  return false;
};

export const unsensitiveIsSameString = (string1: string, string2: string) =>
  string1.toUpperCase().localeCompare(string2.toUpperCase(), "fr", {
    sensitivity: "base",
  }) === 0;

export const isNumeric = (value: any) => /^\d+$/.test(value);

export const emailRegexp =
  /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
export const nameRegexp = /^[A-Z0-9\- \W]+$/; // capital letters, numbers, hyphen & special characters
export const NAME_ERROR_MSG =
  "Veuillez saisir un nom comprenant des majuscules, chiffres, caractères spéciaux et espaces uniquement.";

export const DEFAULT_DATE_FORMAT = "dd/mm/yyyy";
export const DEFAULT_HOUR_FORMAT = "HH:MM:ss";

export const toTime = (hour?: number) => {
  if (!hour) {
    return "";
  }
  const h = `${Math.floor(hour)}`;
  const mn = `${(hour - Math.floor(hour)) * 60}`;
  return `${h.padStart(2, "0")}h${mn.padStart(2, "0")}`;
};

export const removeDuplicates = (from: string[]): string[] =>
  from?.filter((el: string, index: number) => from?.indexOf(el) === index);

/**
 * TypeScript can't return the enum length by itself.
 * Object.keys(enumObject).length is not enoush, see :
 * https://stackoverflow.com/questions/38034673/determine-the-number-of-enum-elements-typescript
 */
export const getNumberEnumLength = (enumObject: any): number => {
  return Object.keys(enumObject).reduce(
    (count, item) => (Number.isNaN(Number(item)) ? count + 1 : count),
    0
  );
};

export const generateArrayFrom0To = (finalNumber: number): number[] =>
  Array.from({ length: finalNumber + 1 }, (_, i) => i);

// See https://github.com/Automattic/mongoose/issues/1959#issuecomment-97457325
export const isAnObjectId = (value?: string) =>
  value?.match(/^[0-9a-fA-F]{24}$/) ||
  value?.match(
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/
  );

/**
 * Normalizes the given text by converting it to lowercase, applying NFD normalization,
 * removing diacritics and trimming any whitespace.
 */
export const normalizeText = (text: string, applyTrimming = true) => {
  if (!text) return "";
  let res = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (applyTrimming) {
    res = res.trim();
  }
  return res;
};

export const alphabeticalSortByKey = (array: any[], key: string) => {
  return array.sort((a: any, b: any) => {
    const keyA = normalizeText(a?.[key]);
    const keyB = normalizeText(b?.[key]);
    return keyA.localeCompare(keyB);
  });
};

export const normalizeName = (text: string) =>
  normalizeText(text, false).toUpperCase();

export const getObjectFromArray = <T>(
  objectArray: T[],
  id: string,
  idKeyName = "_id"
) => objectArray.find((object) => object?.[idKeyName] === id);

export const getUTCDate = (dateString) => {
  const date = new Date(dateString);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

/**
 * Return the date formated. Ex : '2024-12-19_16h40'
 * @param dateString a string that new Date(string) constructor accepts.
 * If undefined the current date is used
 * @returns
 */
export const formatDateTime = (dateString?: string) => {
  const d = !dateString ? new Date() : new Date(dateString);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); // Corrige le décalage UTC
  return d.toISOString().slice(0, 16).replace(/T/g, "_").replace(/:/g, "h");
};

/** Ensures carrier tracking URLs open correctly (e.g. www.example.com → https://www.example.com). */
export const normalizeCarrierTrackingLink = (link: string): string => {
  const trimmed = link.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/\//, "")}`;
};

export function uniqueID() {
  return Math.floor(Math.random() * Date.now());
}

// Helper function to normalize any potential array-like data
export const normalizeArray = (data: any): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.data && Array.isArray(data.data)) return data.data;
  if (typeof data === "object" && data !== null) {
    // Try to convert to array if it's an object with numeric keys
    const values = Object.values(data);
    if (values.length > 0) return values;
  }
  return [];
};
